'use client';

import { useQuery } from '@tanstack/react-query';
import {
  getProducts,
  getProductsByDeliveryPoint,
  getProductById,
  getSetIngredients,
  getGiftRolls,
  type ProductWithCategories,
} from '@/lib/api/products';

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...productKeys.lists(), filters] as const,
  byDeliveryPoint: (pointId: number) => [...productKeys.all, 'delivery-point', pointId] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: number) => [...productKeys.details(), id] as const,
  setIngredients: (setId: number) => [...productKeys.all, 'set-ingredients', setId] as const,
  giftRolls: () => [...productKeys.all, 'gift-rolls'] as const,
};

/**
 * Hook to fetch all products with categories
 */
export function useProducts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: productKeys.lists(),
    queryFn: getProducts,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    ...options,
  });
}

/**
 * Hook to fetch products by delivery point
 */
export function useProductsByDeliveryPoint(
  pointId: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: productKeys.byDeliveryPoint(pointId),
    queryFn: () => getProductsByDeliveryPoint(pointId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!pointId,
    ...options,
  });
}

/**
 * Hook to fetch single product by ID
 */
export function useProduct(id: number, options?: { enabled?: boolean; includeHidden?: boolean }) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => getProductById(id, options?.includeHidden),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch set ingredients
 */
export function useSetIngredients(
  setId: number,
  options?: { enabled?: boolean; includeHidden?: boolean }
) {
  return useQuery({
    queryKey: productKeys.setIngredients(setId),
    queryFn: () => getSetIngredients(setId, options?.includeHidden),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!setId,
    ...options,
  });
}

/**
 * Hook to fetch gift rolls
 */
export function useGiftRolls(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: productKeys.giftRolls(),
    queryFn: getGiftRolls,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
}

/**
 * Group products by category ID
 */
export function useProductsByCategory(products: ProductWithCategories[] | undefined) {
  if (!products) return new Map();

  const grouped = new Map<
    number,
    { category: { id: number; name: string }; products: ProductWithCategories[] }
  >();

  products.forEach((product) => {
    product.categories.forEach((category) => {
      if (!grouped.has(category.id)) {
        grouped.set(category.id, {
          category,
          products: [],
        });
      }
      grouped.get(category.id)!.products.push(product);
    });
  });

  return grouped;
}

